import { useMemo } from 'react';
import useAllDeviceItems from './useAllDeviceItems';

/**
 * Returns the set of pet GraphQL IDs for pets the current rescue has fully
 * adopted out (COMPLETED_ADOPTIONS). Callers cross-reference this set against
 * chip / device rows to gate mutation actions on adopted pets.
 *
 * Scope: backed by `rescueDevices`, which only enumerates pets that ever had
 * a GPS device. Chip-only adoptions (no device ever attached) won't appear
 * here — those flows rely on the chip's own `assignedTo === 'adopter'` signal
 * to gate the destructive Clear Owner action. If chip-only adoption tracking
 * becomes a product requirement, swap this source for a pet-centric query.
 *
 * `loading` is true until the first page has resolved. Consumers should treat
 * loading as "adoption status unknown" and avoid showing irreversible mutation
 * actions for rows that have a pet attached until it flips to false.
 */
export default function useAdoptedPetIds(): { adoptedPetIds: Set<string>; loading: boolean } {
  const { items, loading } = useAllDeviceItems('COMPLETED_ADOPTIONS');
  const adoptedPetIds = useMemo(() => new Set(items.map((item) => item.pet.id)), [items]);
  return { adoptedPetIds, loading };
}
